// M2V.io - Router Configuration
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Layouts
import DefaultLayout from '@/layouts/DefaultLayout.vue'

// Views
import HomeView from '@/views/HomeView.vue'
import GalleryView from '@/views/GalleryView.vue'
import PricingView from '@/views/PricingView.vue'
import LoginView from '@/views/LoginView.vue'
import AuthCallbackView from '@/views/AuthCallbackView.vue'
import DashboardView from '@/views/DashboardView.vue'
import ProfileView from '@/views/ProfileView.vue'
import ProjectCreateView from '@/views/ProjectCreateView.vue'
import ProjectView from '@/views/ProjectView.vue'
import TermsView from '@/views/TermsView.vue'
import PrivacyView from '@/views/PrivacyView.vue'
import ContactView from '@/views/ContactView.vue'
import FaqView from '@/views/FaqView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public routes
    {
      path: '/',
      component: DefaultLayout,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          meta: { title: 'M2V.io - Transformez votre musique en vidéo' }
        },
        {
          path: 'gallery',
          name: 'gallery',
          component: GalleryView,
          meta: { title: 'Galerie - M2V.io' }
        },
        {
          path: 'pricing',
          name: 'pricing',
          component: PricingView,
          meta: { title: 'Tarifs - M2V.io' }
        },
        {
          path: 'terms',
          name: 'terms',
          component: TermsView,
          meta: { title: 'Conditions d\'utilisation - M2V.io' }
        },
        {
          path: 'privacy',
          name: 'privacy',
          component: PrivacyView,
          meta: { title: 'Politique de confidentialité - M2V.io' }
        },
        {
          path: 'contact',
          name: 'contact',
          component: ContactView,
          meta: { title: 'Contact - M2V.io' }
        },
        {
          path: 'faq',
          name: 'faq',
          component: FaqView,
          meta: { title: 'FAQ - M2V.io' }
        },
        {
          path: 'login',
          name: 'login',
          component: LoginView,
          meta: { title: 'Connexion - M2V.io', guest: true }
        },
        {
          path: 'auth/callback',
          name: 'auth-callback',
          component: AuthCallbackView,
          meta: { title: 'Connexion en cours...' }
        },
        
        // Protected routes
        {
          path: 'dashboard',
          name: 'dashboard',
          component: DashboardView,
          meta: { title: 'Tableau de bord - M2V.io', requiresAuth: true }
        },
        {
          path: 'profile',
          name: 'profile',
          component: ProfileView,
          meta: { title: 'Mon profil - M2V.io', requiresAuth: true }
        },
        {
          path: 'project/new',
          name: 'project-create',
          component: ProjectCreateView,
          meta: { title: 'Nouveau projet - M2V.io', requiresAuth: true }
        },
        {
          path: 'project/:id',
          name: 'project',
          component: ProjectView,
          meta: { title: 'Projet - M2V.io', requiresAuth: true }
        }
      ]
    },
    
    // Catch-all redirect
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Update page title
  if (to.meta.title) {
    document.title = to.meta.title as string
  }
  
  const authStore = useAuthStore()
  
  // Wait for auth initialization
  if (!authStore.initialized) {
    await authStore.init()
  }
  
  // Check authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }
  
  // Redirect authenticated users from guest pages
  if (to.meta.guest && authStore.isAuthenticated) {
    return next({ name: 'dashboard' })
  }
  
  next()
})

export default router
